/**
 * Which cash-refund tiers a private lesson cancellation policy can actually
 * grant, derived from the same ladder `computeBookingRefundDecision`
 * (services/private-lessons/cancellationRefundRules.ts) walks for a family
 * cancellation. Every screen that shows the policy reads this, so a tier is
 * shown only when the resolver can pay it.
 */

export interface RefundCutoffFields {
  full_refund_hours?: number | string | null;
  partial_refund_hours?: number | string | null;
  partial_refund_percent?: number | string | null;
}

export interface RefundPolicySummary {
  /** Hours of notice for a 100% refund. `0` = any time before the lesson starts. */
  fullHours: number | null;
  /** The partial tier, only when it can pay something before the full tier does. */
  partial: { hours: number; percent: number } | null;
  /** No tier can pay: every family cancellation gets no automatic cash refund. */
  noAutomaticRefunds: boolean;
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function summarizeRefundPolicy(policy: RefundCutoffFields | null | undefined): RefundPolicySummary {
  const fullHours = toNumberOrNull(policy?.full_refund_hours);
  const partialHours = toNumberOrNull(policy?.partial_refund_hours);
  const partialPercent = toNumberOrNull(policy?.partial_refund_percent) ?? 0;

  // The resolver checks the full tier first, so a partial cutoff at or above
  // the full cutoff never pays.
  const partialReachable =
    partialHours != null && partialPercent > 0 && (fullHours == null || partialHours < fullHours);

  const partial = partialReachable ? { hours: partialHours, percent: partialPercent } : null;

  return {
    fullHours,
    partial,
    noAutomaticRefunds: fullHours == null && partial == null,
  };
}

/** Empty number box → `null` (the stored "not set" value), never `NaN`. */
export function parseCutoffInput(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}
